/**
 * Tutorial Spotlight Component
 * Provides a guided overlay system that highlights specific UI elements
 * during tutorial quests
 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Space, Typography, Progress } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
  CheckCircleFilled
} from "@ant-design/icons";
import styles from "./TutorialQuests.module.css";
import { STEP_TARGETS } from "./spotlightTargets";

const { Text, Paragraph } = Typography;

const getSvgElementRect = (element) => {
  if (typeof SVGGraphicsElement === 'undefined' || !(element instanceof SVGGraphicsElement)) {
    return null;
  }

  try {
    const box = element.getBBox();
    const matrix = element.getScreenCTM();

    if (!matrix || !box.width || !box.height || typeof DOMPoint === 'undefined') {
      return null;
    }

    const topLeft = new DOMPoint(box.x, box.y).matrixTransform(matrix);
    const bottomRight = new DOMPoint(box.x + box.width, box.y + box.height).matrixTransform(matrix);

    return {
      x: Math.min(topLeft.x, bottomRight.x),
      y: Math.min(topLeft.y, bottomRight.y),
      width: Math.abs(bottomRight.x - topLeft.x),
      height: Math.abs(bottomRight.y - topLeft.y),
      found: true,
    };
  } catch (error) {
    return null;
  }
};

/**
 * Get element position with fallback
 */
const getElementRect = (step) => {
  const target = step?.spotlightTarget || STEP_TARGETS[step?.id];
  if (!target) return null;
  const fallbackRect = target.fallbackRect || target.fallback;

  // Try to find the element using selector
  try {
    const element = document.querySelector(target.selector);
    if (element) {
      const rect = element.getBoundingClientRect();

      if (rect.width > 0 && rect.height > 0) {
        return {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
          found: true,
        };
      }

      const svgRect = getSvgElementRect(element);
      if (svgRect) {
        return svgRect;
      }
    }
  } catch (e) {
    // Selector might be invalid
  }

  // Use fallback position
  if (fallbackRect) {
    return { ...fallbackRect, found: false };
  }

  return null;
};

/**
 * Spotlight Overlay Component
 * Darkens the screen except for the highlighted area
 */
export const TutorialSpotlight = ({
  isActive,
  quest,
  currentStepIndex,
  onNext,
  onPrevious,
  onClose,
  onComplete,
  isQuestCompleted,
}) => {
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const overlayRef = useRef(null);

  const currentStep = quest?.steps?.[currentStepIndex];
  const totalSteps = quest?.steps?.length || 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isFirstStep = currentStepIndex === 0;

  // Update target position
  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetRect(null);
      return;
    }

    const updatePosition = () => {
      const rect = getElementRect(currentStep);
      if (rect) {
        // Add padding around the element
        const padding = 10;
        const targetRectWithPadding = {
          x: rect.x - padding,
          y: rect.y - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        };
        setTargetRect(targetRectWithPadding);

        // Calculate tooltip position
        const target = currentStep.spotlightTarget || STEP_TARGETS[currentStep.id];
        const tooltipWidth = 380;
        const tooltipHeight = 420; // Account for full content height
        const gap = 20; // Gap between tooltip and target
        
        // Helper to check if tooltip overlaps target
        const checkOverlap = (tx, ty) => {
          const tooltipRight = tx + tooltipWidth;
          const tooltipBottom = ty + tooltipHeight;
          const targetRight = targetRectWithPadding.x + targetRectWithPadding.width;
          const targetBottom = targetRectWithPadding.y + targetRectWithPadding.height;
          
          return !(tx > targetRight || tooltipRight < targetRectWithPadding.x || 
                   ty > targetBottom || tooltipBottom < targetRectWithPadding.y);
        };

        // Try positions in order of preference based on the configured position
        const positions = {
          top: () => ({
            x: rect.x + rect.width / 2 - tooltipWidth / 2,
            y: rect.y - tooltipHeight - gap
          }),
          bottom: () => ({
            x: rect.x + rect.width / 2 - tooltipWidth / 2,
            y: rect.y + rect.height + gap
          }),
          left: () => ({
            x: rect.x - tooltipWidth - gap,
            y: rect.y + rect.height / 2 - tooltipHeight / 2
          }),
          right: () => ({
            x: rect.x + rect.width + gap,
            y: rect.y + rect.height / 2 - tooltipHeight / 2
          })
        };

        // Order to try positions (configured position first, then alternatives)
        const preferredPosition = target?.position || "right";
        const positionOrder = [preferredPosition, "left", "right", "top", "bottom"]
          .filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates

        let finalPos = null;

        for (const pos of positionOrder) {
          const { x: tx, y: ty } = positions[pos]();
          
          // Clamp to viewport
          const clampedX = Math.max(10, Math.min(tx, window.innerWidth - tooltipWidth - 10));
          const clampedY = Math.max(10, Math.min(ty, window.innerHeight - tooltipHeight - 10));
          
          // Check if this position overlaps the target
          if (!checkOverlap(clampedX, clampedY)) {
            finalPos = { x: clampedX, y: clampedY };
            break;
          }
        }

        // If all positions overlap (shouldn't happen), just use top-left corner
        if (!finalPos) {
          finalPos = { x: 10, y: 10 };
        }

        setTooltipPosition(finalPos);
      }
    };

    updatePosition();

    // Update on resize/scroll
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);
    
    // Periodic update to catch dynamic elements
    const interval = setInterval(updatePosition, 500);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
      clearInterval(interval);
    };
  }, [isActive, currentStep]);

  if (!isActive || !quest || !currentStep) return null;

  const progressPercent = Math.round(((currentStepIndex + 1) / totalSteps) * 100);

  return (
    <div className={styles.spotlightOverlay} ref={overlayRef}>
      {/* Dark overlay with cutout */}
      <svg className={styles.spotlightSvg}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.x}
                y={targetRect.y}
                width={targetRect.width}
                height={targetRect.height}
                rx="8"
                ry="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.7)"
          mask="url(#spotlight-mask)"
        />
        {/* Highlight border around target */}
        {targetRect && (
          <rect
            x={targetRect.x}
            y={targetRect.y}
            width={targetRect.width}
            height={targetRect.height}
            rx="8"
            ry="8"
            fill="none"
            stroke="#1890ff"
            strokeWidth="3"
            className={styles.spotlightBorder}
          />
        )}
      </svg>

      {/* Tooltip/Instruction Card */}
      <div
        className={styles.spotlightTooltip}
        style={{
          left: tooltipPosition.x,
          top: tooltipPosition.y,
        }}
      >
        {/* Header */}
        <div className={styles.spotlightHeader}>
          <div className={styles.spotlightTitleRow}>
            <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
              Step {currentStepIndex + 1}: {currentStep.task}
            </Text>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={onClose}
              className={styles.spotlightCloseBtn}
            />
          </div>
          <Progress
            percent={progressPercent}
            size="small"
            showInfo={false}
            strokeColor={{ "0%": "#1890ff", "100%": "#52c41a" }}
            style={{ marginTop: 8 }}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {currentStepIndex + 1} of {totalSteps} steps in "{quest.title}"
          </Text>
        </div>

        {/* Content */}
        <div className={styles.spotlightContent}>
          <Paragraph style={{ marginBottom: 12, lineHeight: 1.6, textAlign: 'left' }}>
            {currentStep.instruction}
          </Paragraph>
          
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#f9fafb', 
            borderRadius: 8,
            lineHeight: 1.7,
            fontSize: 13,
            textAlign: 'left'
          }}>
            <Text style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
              {currentStep.detailedGuidance} {currentStep.successIndicator}
            </Text>
          </div>
        </div>

        {/* Navigation */}
        <div className={styles.spotlightFooter}>
          <Button
            disabled={isFirstStep}
            onClick={onPrevious}
            icon={<LeftOutlined />}
          >
            Previous
          </Button>
          
          <Space>
            <Button onClick={onClose}>
              Exit Tour
            </Button>
            
            {isLastStep ? (
              <Button
                type="primary"
                onClick={onComplete}
                icon={<CheckCircleFilled />}
              >
                {isQuestCompleted ? "Complete!" : "Finish Quest"}
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={onNext}
                icon={<RightOutlined />}
                iconPosition="end"
              >
                Next Step
              </Button>
            )}
          </Space>
        </div>
      </div>
    </div>
  );
};

TutorialSpotlight.propTypes = {
  isActive: PropTypes.bool.isRequired,
  quest: PropTypes.object,
  currentStepIndex: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  isQuestCompleted: PropTypes.bool,
};

export default TutorialSpotlight;
